import React from "react";
import {
  FaUserCircle,
  FaClipboardList,
  FaHeartbeat,
  FaCheckCircle,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import colors from "../../constants/colors";
import { TeleconsultCardProps } from "../interface/consult";
import { TELECONSULT_URL } from "../interface/Config";

const TeleconsultCard: React.FC<TeleconsultCardProps> = ({
  teleconsultHistory,
  participantName,
}) => {
  const navigate = useNavigate();
  const [snackbarOpen, setSnackbarOpen] = React.useState(false);

  const refNumber =
    teleconsultHistory?.participants?.subscriber?.[0]?.ref_number;
  // console.log("tele", teleconsultHistory);

  const handleNavigate = (section: string) => {
    switch (section) {
      case "Summary":
        const publisherToken =
          teleconsultHistory?.participants?.publisher?.[0]?.token;
        if (publisherToken) {
          navigate(
            `/teleconsultsummary/${publisherToken}`
          );
        } else {
          console.error("Publisher token is missing or undefined.");
        }
        break;
      case "Health":
        if (refNumber) {
          navigate(`/HealthVitals/${refNumber}`);
        } else {
          console.error("Reference number is missing or undefined.");
        }
        break;
      case "Status":
        const teleconsultId = teleconsultHistory?.id;
        const statusToken =
          teleconsultHistory?.participants?.publisher?.[0]?.token;
        if (teleconsultId && statusToken) {
          window.open(
           // `${TELECONSULT_URL}/teleconsult/${statusToken}`,
           `https://teleconsult.a2zhealth.in/teleconsult/${statusToken}`,
            "_blank"
          );
        } else {
          console.error(
            "Teleconsult history ID or publisher token is missing or undefined."
          );
        }
        break;
      default:
        console.error("Invalid section specified.");
        break;
    }
  };

  const handleSnackbarClose = () => setSnackbarOpen(false);

  const consultId = teleconsultHistory?.id || "Unknown ID";
  const subscriberName =
    teleconsultHistory?.participants?.subscriber?.[0]?.participant_info?.name;
  const subscriberPhoneNumber =
    teleconsultHistory?.participants?.subscriber?.[0]?.participant_info?.phone;
  const subscriberEmail =
    teleconsultHistory?.participants?.subscriber?.[0]?.participant_info?.email;
  const subscriberGender =
    teleconsultHistory?.participants?.subscriber?.[0]?.participant_info?.gender;
  const consultNotes =
    teleconsultHistory?.consult_status?.name || "No notes available";

  const getConsultNotesIconColor = (status: string) => {
    switch (status) {
      case "New":
        return "bg-blue-500";
      case "Started":
        return "bg-[#ffae42]";
      case "Ended":
        return "bg-green-500";
      case "Cancelled":
        return "bg-red-500";
      case "Waiting":
        return "bg-yellow-500";
      default:
        return "bg-gray-300";
    }
  };

  return (
    <div className="bg-gradient-to-b from-white to-gray-100 border rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300">
      <div className="p-5">
        <div className="flex flex-col md:flex-row md:items-start">
          {/* User Info */}
          <div className="flex items-center md:space-x-4 flex-wrap md:flex-nowrap">
            <div className="w-14 h-14 rounded-full bg-gray-300 flex items-center justify-center mb-4 md:mb-0">
              <FaUserCircle className="text-gray-700 text-3xl" />
            </div>

            <div className="w-full md:w-auto" style={{ minWidth: '280px' }}>
              <p className="flex justify-between items-center w-full">
                <h2 className="font-semibold text-gray-800 flex items-center">
                  <span className="font-medium text-gray-600">
                    <span className="font-bold">{subscriberName}</span>
                    {teleconsultHistory?.participants?.subscriber?.[0]?.participant_info?.additional_info?.age ||
                      subscriberGender ? (
                      <>
                        {/* Conditionally show Age */}
                        {teleconsultHistory?.participants?.subscriber?.[0]?.participant_info?.additional_info?.age && (
                          <>
                            {" | "}
                            <span className="text-sm text-gray-400">
                              {teleconsultHistory.participants.subscriber[0].participant_info.additional_info.age} Yrs
                            </span>
                          </>
                        )}

                        {/* Conditionally show Gender */}
                        {subscriberGender && (
                          <>
                            {" | "}
                            <span className="text-sm text-gray-400">
                              <span>{subscriberGender === "Male" ? "M" : "F"}</span>
                            </span>
                          </>
                        )}
                      </>
                    ) : null}
                  </span>
                </h2>

                {/* Right-aligned Consult ID */}
                <div
                  className={`text-right text-white px-1 rounded-lg ${getConsultNotesIconColor(
                    consultNotes
                  )}`}
                >
                  <p className="font-semibold">{consultId}</p>
                </div>
              </p>

              <p className="text-gray-600">
                <span className="font-small font-semibold">Phone:</span>{" "}
                <span className="text-sm">{subscriberPhoneNumber ? subscriberPhoneNumber : "NA"}</span>
              </p>

              <p className="text-gray-600">
                <span className="font-small font-semibold">Email:</span>{" "}
                <span className="text-sm">{subscriberEmail ? subscriberEmail : "NA"}</span>
              </p>

              <p className="text-gray-600">
                <span className="font-small font-semibold">Reason:</span>{" "}
                <span className="text-sm">{teleconsultHistory?.reason || "Not provided"}</span>
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="mt-4 md:mt-6 flex flex-col md:flex-row divide-y md:divide-y-0 md:divide-x divide-gray-300">
          <button
            className="py-2 text-center text-gray-800 hover:bg-gray-100 transition md:flex-1"
            onClick={() => handleNavigate("Summary")}
          >
            <FaClipboardList className="inline-block mr-2" />
            Summary
          </button>
          <button
            className="py-2 text-center text-gray-800 hover:bg-gray-100 transition md:flex-1"
            onClick={() => handleNavigate("Health")}
          >
            <FaHeartbeat className="inline-block mr-2 text-green-600" />
            Health
          </button>
          <button
            className={`flex-1 text-center rounded-tl-md rounded-bl-md rounded-br-md text-white transition ${getConsultNotesIconColor(consultNotes)}`}
            onClick={() => handleNavigate("Status")}
            disabled={
              teleconsultHistory?.consult_status?.name === "Ended" ||
              teleconsultHistory?.consult_status?.name === "Cancelled"
            }
          >
            <FaCheckCircle className="inline-block mr-2" />
            {consultNotes}
          </button>
        </div>

        {/* Snackbar */}
        <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleSnackbarClose}>
          <Alert onClose={handleSnackbarClose} severity="info" sx={{ width: "100%" }}>
            Teleconsult status updated!
          </Alert>
        </Snackbar>
      </div>
    </div>


  );
};

export default TeleconsultCard;

